import React, { useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import './PracticeProfileImages.scss';
import { UploadPracticeProfileImage } from '../../slices/profiles-data-slice/thunks/upload-practice-profile-image.thunk';
import { RemovePracticeProfileImage } from '../../slices/profiles-data-slice/thunks/remove-practice-profile-image.thunk';
import { ReorderPracticeProfileImages } from '../../slices/profiles-data-slice/thunks/reorder-practice-profile-images.thunk';
import { PracticeProfileImage } from '../../slices/profiles-data-slice/models/practice-profile-image';
import ConfirmationModal from '../Modal/ConfirmationModal';

interface PracticeProfileImagesProps {
    images: PracticeProfileImage[];
    profileId: string;
    practiceProfileId: string;
    locationId: string;
    maxImages?: number;
    disabled?: boolean;
    className?: string;
}

const PracticeProfileImages: React.FC<PracticeProfileImagesProps> = ({
    images = [],
    profileId,
    practiceProfileId,
    locationId,
    maxImages = 10,
    disabled = false,
    className = '',
}) => {
    const dispatch = useDispatch() as any;
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragOver, setDragOver] = useState(false);
    const [imageToRemove, setImageToRemove] =
        useState<PracticeProfileImage | null>(null);

    // Ordering: optimistic local order (ids) shown immediately on drop/arrow,
    // while the reorder thunk persists and the profile refetch catches up.
    const [localOrder, setLocalOrder] = useState<number[] | null>(null);
    const [draggedImageId, setDraggedImageId] = useState<number | null>(null);

    const orderedImages = useMemo(() => {
        const bySortOrder = [...images].sort(
            (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
        );
        if (!localOrder) return bySortOrder;
        // Local order only applies while it covers the exact same image set.
        const ids = new Set(bySortOrder.map((i) => i.id));
        if (
            localOrder.length !== bySortOrder.length ||
            localOrder.some((id) => !ids.has(id))
        ) {
            return bySortOrder;
        }
        const byId = new Map(bySortOrder.map((i) => [i.id, i]));
        return localOrder.map((id) => byId.get(id)!);
    }, [images, localOrder]);

    const persistOrder = (idsInOrder: number[]) => {
        setLocalOrder(idsInOrder);
        dispatch(
            ReorderPracticeProfileImages({
                profileId,
                practiceProfileId,
                locationId,
                uploadIdsInOrder: idsInOrder,
            })
        );
    };

    const moveImage = (fromIndex: number, toIndex: number) => {
        if (
            disabled ||
            toIndex < 0 ||
            toIndex >= orderedImages.length ||
            fromIndex === toIndex
        ) {
            return;
        }
        const ids = orderedImages.map((image) => image.id);
        const [moved] = ids.splice(fromIndex, 1);
        ids.splice(toIndex, 0, moved);
        persistOrder(ids);
    };

    const handleImageDrop = (targetIndex: number) => {
        if (draggedImageId == null) return;
        const fromIndex = orderedImages.findIndex(
            (image) => image.id === draggedImageId
        );
        setDraggedImageId(null);
        if (fromIndex >= 0) {
            moveImage(fromIndex, targetIndex);
        }
    };

    const handleFileSelect = (files: FileList | null) => {
        if (!files || disabled) return;

        const validFiles: File[] = [];
        const imageExtensions = [
            'jpg',
            'jpeg',
            'png',
            'gif',
            'bmp',
            'webp',
            'svg',
        ];

        Array.from(files).forEach((file) => {
            const extension = file.name.split('.').pop()?.toLowerCase();
            if (extension && imageExtensions.includes(extension)) {
                validFiles.push(file);
            }
        });

        if (validFiles.length === 0) {
            alert(
                'Please select valid image files (jpg, jpeg, png, gif, bmp, webp, svg)'
            );
            return;
        }

        if (images.length + validFiles.length > maxImages) {
            alert(
                `Maximum ${maxImages} images allowed. You can add ${
                    maxImages - images.length
                } more images.`
            );
            return;
        }
        if (!locationId) {
            alert('Add a location to the practice before uploading images.');
            return;
        }
        dispatch(
            UploadPracticeProfileImage({
                profileId: profileId,
                practiceProfileId: practiceProfileId,
                locationId: locationId,
                files: validFiles,
            })
        );
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleFileSelect(e.target.files);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveImage = (image: PracticeProfileImage) => {
        if (disabled || !image.id) return;
        setImageToRemove(image);
    };

    const handleRemoveConfirmed = () => {
        if (!imageToRemove) return;

        dispatch(
            RemovePracticeProfileImage({
                profileId,
                practiceProfileId,
                uploadId: imageToRemove.id,
            })
        );
        setImageToRemove(null);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        if (!disabled) {
            setDragOver(true);
        }
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        // A tile being reordered must not be treated as a file drop.
        if (!disabled && draggedImageId == null) {
            handleFileSelect(e.dataTransfer.files);
        }
    };

    const handleUploadClick = () => {
        if (!disabled && fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    return (
        <div
            className={`image-management ${className} ${
                disabled ? 'disabled' : ''
            }`}>
            <div
                className={`upload-area ${dragOver ? 'drag-over' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleUploadClick}>
                <div className="upload-content">
                    <p className="upload-text">
                        {dragOver
                            ? 'Drop images here'
                            : 'Click to upload or drag and drop images'}
                    </p>
                </div>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.gif,.bmp,.webp,.svg,.JPG,.JPEG,.PNG,.GIF,.BMP,.WEBP,.SVG"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
            />

            {orderedImages.length > 0 && (
                <div className="images-grid">
                    {orderedImages.map((image, index) => (
                        <div
                            key={image.id || index}
                            className={`image-item ${
                                draggedImageId === image.id
                                    ? 'image-item--dragging'
                                    : ''
                            }`}
                            draggable={!disabled && !!image.id}
                            onDragStart={(e) => {
                                setDraggedImageId(image.id);
                                e.dataTransfer.effectAllowed = 'move';
                            }}
                            onDragEnd={() => setDraggedImageId(null)}
                            onDragOver={(e) => {
                                if (draggedImageId != null) e.preventDefault();
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleImageDrop(index);
                            }}>
                            <img
                                src={image.source}
                                alt={
                                    image.name || `Practice image ${index + 1}`
                                }
                            />
                            {index === 0 && orderedImages.length > 1 && (
                                <span className="cover-badge">Cover</span>
                            )}
                            {!disabled && image.id && (
                                <button
                                    className="remove-button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveImage(image);
                                    }}
                                    type="button"></button>
                            )}
                            {!disabled && image.id && orderedImages.length > 1 && (
                                <div className="order-controls">
                                    <button
                                        type="button"
                                        className="order-arrow"
                                        aria-label="Move image left"
                                        disabled={index === 0}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            moveImage(index, index - 1);
                                        }}>
                                        ‹
                                    </button>
                                    <button
                                        type="button"
                                        className="order-arrow"
                                        aria-label="Move image right"
                                        disabled={
                                            index === orderedImages.length - 1
                                        }
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            moveImage(index, index + 1);
                                        }}>
                                        ›
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {imageToRemove && (
                <ConfirmationModal
                    isVisible={true}
                    setIsVisible={(isVisible: boolean) => {
                        if (!isVisible) setImageToRemove(null);
                    }}
                    header={'Image Removal'}
                    confirmationText={`Are you sure you want to remove ${
                        imageToRemove.name || 'this image'
                    }?`}
                    onConfirm={handleRemoveConfirmed}
                    nextButtonText={'Remove'}
                    cancelButtonText={'Cancel'}
                    params={null}
                />
            )}
        </div>
    );
};

export default PracticeProfileImages;
